import { CSSProperties, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getOperation, useOperation } from "../../../../../../../../contexts/OperationContext";
import { selectOpMode } from "../../../../../../../../util/store/selectors";
import { OPERATION_TYPE } from "../../../../../../../../util/varibles/constants";
import { getStartFinishByOpMode } from "../../../../../../../../util/varibles/global";
import { IOperation, IPropsChildren } from "../../../../../../../../util/varibles/interface";
import { EL_RULE } from "../../../../constants";
import { HEIGHT_PER_DAY } from "../../../constants";
import styles from "../../style.module.scss";
import { calcWidth, getHeightOfOp } from "../constants";
import TimeLine from "../TimeLine";

interface IProps extends IPropsChildren {
    event?: any,
    operationType: OPERATION_TYPE,
    data: IOperation
}

type OperationStyle = CSSProperties & {
    '--start-op': number;
    '--min-height': string;
};

export default function SizeOperation({ children, event, operationType, data }: IProps) {
    const { ref } = event || {};
    const [minHeight, setMinHeight] = useState(0);

    useEffect(() => {
        const element = ref?.current as HTMLElement | null;
        if (!element) {
            return;
        }

        const updateMinHeight = () => {
            const nextHeight = getHeightOfOp({ element }) ?? 42;
            setMinHeight(currentHeight => currentHeight === nextHeight ? currentHeight : nextHeight);
        };

        updateMinHeight();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(updateMinHeight);
        const observeCurrentContent = () => {
            observer.disconnect();

            const content = element.lastElementChild;
            if (content) {
                observer.observe(content);
            }

            updateMinHeight();
        };

        observeCurrentContent();

        const mutationObserver = typeof MutationObserver === 'undefined'
            ? undefined
            : new MutationObserver(observeCurrentContent);
        mutationObserver?.observe(element, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            mutationObserver?.disconnect();
        };
    }, [ref, data.operation.note]);

    const operation_mode = useSelector(selectOpMode);
    const get = getOperation();
    const [level] = useOperation(state => state.level)
    const [width = 0] = useOperation(state => state.width)
    const [modeDrag] = useOperation(state => state.modeDrag);

    const [left] = useOperation(state => state.left);
    const [index] = useOperation(state => state.vesselIndex);

    const [activityLog = data.activity_log] = useOperation(state => state.activityLogs[state.rootKey]);
    const { start, finish } = getStartFinishByOpMode[operation_mode](activityLog, data.operation.status);

    const height = (finish - start) / 86400000 * HEIGHT_PER_DAY;

    const adjust = level.ids.length > 0 ? calcWidth[`${level.main}`](level, width, index) : {};
    const isMin = height <= minHeight;
    const isHover = isMin && modeDrag !== 'move';
    const isHide = height < 28;

    const style: CSSProperties & { left: string } = {
        maxHeight: height + 'px',
        width: `calc(${width}% - 20px)`,
        left: `calc(${width * index}% + 10px)`,
        ...adjust
    };

    if (left !== undefined)
        style.left = left + 'px'

    const { isOwn = false, isDrag = false } = get();

    return <>
        <div
            {...event}
            id={data.operation.id}
            className={styles.operation}
            data-rule={EL_RULE.OPERATION}
            style={{ ...style, '--start-op': start, '--min-height': minHeight + 'px' } as OperationStyle}
            data-is-own={isOwn}
            data-is-drag={isDrag}
            data-operation-type={operationType}
            data-mode-drag={modeDrag}
            data-hover={isHover}
            data-mini={isHide}
        >
            {children}
        </div>
        <TimeLine {...{
            isOwn,
            width,
            data,
            adjust,
            activityLog,
        }} />
    </>
}
